import { FC, useMemo } from 'react';
import { Tag } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { ColumnDef } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { DataTable } from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';
import { allDocumentsAtom, allDocumentsFilterAtom } from '../atoms';
import { extractionStatusColors, type ProvisioningDocument } from '../types';
import { DOCUMENT_LINK_COLUMNS } from '../document-columns';
import styles from '../page.module.scss';

export const AllDocumentsTable: FC<{ batchId: number }> = ({ batchId }) => {
  const [docsLoadable] = useAtom(allDocumentsAtom);
  const [filters, setFilters] = useAtom(allDocumentsFilterAtom);

  const columns = useMemo<ColumnDef<ProvisioningDocument>[]>(
    () => [
      ...DOCUMENT_LINK_COLUMNS,
      {
        id: 'providerBatchId',
        accessorKey: 'providerBatchId',
        header: 'Provider Batch ID',
        size: 280,
        enableSorting: false,
        cell: (info) => (
          <span className={styles.batchId}>
            {(info.getValue() as string) || (
              <span className={styles.muted}>—</span>
            )}
          </span>
        ),
      },
      {
        id: 'extractionStatus',
        accessorKey: 'extractionStatus',
        header: 'Extraction',
        size: 110,
        cell: (info) => {
          const isDirect = info.row.original.pipelinePath === 'direct';
          if (isDirect) {
            return (
              <Tag type="teal" size="sm">
                Skipped
              </Tag>
            );
          }
          const status = info.getValue() as string;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'completionStatus',
        accessorKey: 'completionStatus',
        header: 'Completion',
        size: 110,
        cell: (info) => {
          const isDirect = info.row.original.pipelinePath === 'direct';
          if (isDirect) {
            return (
              <Tag type="teal" size="sm">
                Skipped
              </Tag>
            );
          }
          const status = info.getValue() as string;
          if (!status) return <span className={styles.muted}>—</span>;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'entitiesCreated',
        accessorKey: 'entitiesCreated',
        header: 'Entities',
        size: 100,
        enableSorting: false,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'locationsCreated',
        accessorKey: 'locationsCreated',
        header: 'Locations',
        size: 105,
        enableSorting: false,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'addressesCreated',
        accessorKey: 'addressesCreated',
        header: 'Addresses',
        size: 105,
        enableSorting: false,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'capacitiesCreated',
        accessorKey: 'capacitiesCreated',
        header: 'Capacities',
        size: 110,
        enableSorting: false,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'citationsCreated',
        accessorKey: 'citationsCreated',
        header: 'Citations',
        size: 100,
        enableSorting: false,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'errorMessage',
        accessorKey: 'errorMessage',
        header: 'Error',
        size: 300,
        enableSorting: false,
        cell: (info) => {
          const msg = info.getValue() as string;
          if (!msg) return <span className={styles.muted}>&#8212;</span>;
          return (
            <span className={styles.errorText} title={msg}>
              {msg}
            </span>
          );
        },
      },
    ],
    []
  );

  const documents = useMemo(() => {
    if (docsLoadable.state !== 'hasData' || !docsLoadable.data) return [];
    return docsLoadable.data.documents || [];
  }, [docsLoadable]);

  const isLoading = docsLoadable.state === 'loading';

  return (
    <div className={styles.tableContainer}>
      <DataTable
        data={documents}
        columns={columns}
        getRowId={(row) => String(row.documentId)}
        isLoading={isLoading}
        persistenceKey={`provisioning-all-documents-table-${batchId}`}
        config={{
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: false,
        }}
        onGlobalSearchChange={(s) => {
          if (s.globalFilter !== filters.search) {
            setFilters({ ...filters, search: s.globalFilter, page: 1 });
          }
        }}
      />

      <Pagination
        page={filters.page}
        pageSize={filters.perPage}
        pageSizes={[25, 50, 100]}
        totalItems={
          docsLoadable.state === 'hasData'
            ? (docsLoadable.data?.pagination.totalCount ?? 0)
            : 0
        }
        onChange={({ page, pageSize }: { page: number; pageSize: number }) => {
          setFilters({ ...filters, page, perPage: pageSize });
        }}
      />
    </div>
  );
};
